<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';

defineOptions({
    name: 'script-compilation-errors'
});

const props = defineProps({
  "messages": { default: (): string[] => [] },
  "scriptLabel": null,
  "scriptDisplayName": { default: '' },
  "currentFieldKey": null,
  "currentScriptPublicId": { default: '' },
  "otherFieldsWithErrors": { default: (): { key: string; label: string }[] => [] },
  "headerLabel": { default: '' }
});

const router = useRouter();
const emit = defineEmits([]);

const baseLabel = (): string =>
{
    return props.scriptDisplayName ? `${props.scriptDisplayName} — ${props.scriptLabel}` : props.scriptLabel;
};

const sectionHeaderText = (section: string): string =>
{
    return section?.trim() ? section : baseLabel();
};

const onNavigate = (fieldKey: string): void =>
{
    emit('navigateToField', fieldKey);
};

type JsonErrorItem = { publicId: string; scriptName: string; filename?: string | null; line: number; col?: number | null; message: string };
type ErrorItem = { fileHeader?: string; line: number; col?: number; message: string; publicId?: string };
type GroupedErrors = { section: string; errors: ErrorItem[]; publicId?: string }[];

const tryParseCompilationErrorsJson = (raw: string): ErrorItem[] | null =>
{
    const sep = '\n\n';
    const idx = raw.indexOf(sep);

    if (idx < 0)
        return null;

    const afterHeader = raw.substring(idx + sep.length).trim();

    if (!afterHeader.startsWith('['))
        return null;

    try
    {
        const arr = JSON.parse(afterHeader) as JsonErrorItem[];

        if (!Array.isArray(arr))
            return null;

        return arr.map(e => ({
            fileHeader: e.filename ? `${e.scriptName || ''} (${e.filename})` : (e.scriptName || e.publicId || ''),
            line: e.line ?? 0,
            col: e.col ?? undefined,
            message: e.message ?? '',
            publicId: e.publicId || undefined
        }));
    }
    catch
    {
        return null;
    }
};

const parseLegacyFormat = (raw: string): ErrorItem[] =>
{
    const items: ErrorItem[] = [];
    const seen = new Set<string>();
    const blocks = raw.split(/\n\n+/).map(b => b.trim()).filter(Boolean);

    for (const block of blocks)
    {
        const numbered = block.match(/^\d+\.\s*(.+)$/s);
        const text = numbered ? numbered[1].trim() : block;

        if (!text)
            continue;

        if (text.match(/^\[\[\[.+\]\]\]/) || text.startsWith('Błąd kompilacji') || text.startsWith('Wystąpił błąd'))
            continue;

        if (seen.has(text))
            continue;

        seen.add(text);

        const locationMatch = text.match(/^(.+?)(\(\d+(?:,\s*\d+)?\))(?:\[\[ref:([^\]]+)\]\])?:\s*(.+)$/);

        if (locationMatch)
        {
            const m = locationMatch[2].match(/\((\d+)(?:,\s*(\d+))?\)/);

            items.push({
                fileHeader: locationMatch[1].trim(),
                line: m ? parseInt(m[1], 10) : 0,
                col: m?.[2] ? parseInt(m[2], 10) : undefined,
                message: locationMatch[4].trim(),
                publicId: locationMatch[3] ?? undefined
            });
        }
        else
        {
            const altMatch = text.match(/^(\(.+?\)):\s*(.+)$/);

            if (altMatch)
            {
                const m = altMatch[1].match(/\((\d+)(?:,\s*(\d+))?\)/);

                items.push({
                    line: m ? parseInt(m[1], 10) : 0,
                    col: m?.[2] ? parseInt(m[2], 10) : undefined,
                    message: altMatch[2].trim()
                });
            }
            else
            {
                items.push({ line: 0, message: text });
            }
        }
    }

    return items;
};

const groupedErrors = computed((): GroupedErrors =>
{
    const allItems: ErrorItem[] = [];

    for (const raw of props.messages ?? [])
    {
        const jsonItems = tryParseCompilationErrorsJson(raw);

        if (jsonItems?.length)
        {
            allItems.push(...jsonItems);

            continue;
        }

        allItems.push(...parseLegacyFormat(raw));
    }

    if (allItems.length === 0 && props.messages?.length)
    {
        const joined = props.messages.join('\n\n');
        const withoutHeader = joined.replace(/^\[\[\[.+\]\]\]\s*\n\n?/i, '').replace(/^(Błąd kompilacji|Wystąpił błąd[^:]*:)\s*\n\n?/i, '').trim();

        allItems.push({ line: 0, message: withoutHeader || joined });
    }

    const bySection = new Map<string, { errors: ErrorItem[]; publicId?: string }>();
    const defaultSection = '';

    for (const item of allItems)
    {
        const section = item.fileHeader ?? defaultSection;
        const entry = bySection.get(section);

        if (entry)
        {
            entry.errors.push(item);

            if (item.publicId && !entry.publicId)
                entry.publicId = item.publicId.trim();
        }
        else
        {
            bySection.set(section, { errors: [item], publicId: item.publicId?.trim() });
        }
    }

    return Array.from(bySection.entries()).map(([section, { errors, publicId }]) => ({ section, errors, publicId }));
});

const onOpenScript = (publicId: string): void =>
{
    const route = router.resolve({ name: 'lowCode-scripts-edit', params: { publicId } });

    window.open(route.href, '_blank');
};
</script>

<template>
    <div v-if="groupedErrors.length > 0" class="script-compilation-errors mt-2">
        <div class="compilation-errors-header">
            <i class="fa-solid fa-circle-exclamation text-danger me-2"></i>
            <span class="fw-semibold">{{ headerLabel || $t('[[[Błędy kompilacji]]]') }}</span>
        </div>
        <div v-if="otherFieldsWithErrors?.length" class="compilation-errors-navigate mb-2">
            <span class="me-1">{{ $t('[[[Przejdź do:]]]') }}</span>
            <span v-for="(field, idx) in otherFieldsWithErrors" :key="field.key" class="compilation-nav-item">
                <span v-if="idx > 0" class="compilation-nav-sep">, </span>
                <button
                    type="button"
                    class="btn btn-link btn-sm p-0 text-decoration-underline"
                    @click="onNavigate(field.key)"
                >
                    {{ field.label }}
                </button>
            </span>
        </div>
        <div
            v-for="(group, gIndex) in groupedErrors"
            :key="gIndex"
            class="compilation-errors-group"
        >
            <div class="compilation-errors-section-header">
                <span>{{ sectionHeaderText(group.section) }}</span>
                <button
                    v-if="group.publicId && group.publicId.toLowerCase() !== (currentScriptPublicId || '').toLowerCase()"
                    type="button"
                    class="btn btn-sm compilation-open-script-btn"
                    :title="$t('[[[Otwórz skrypt]]]')"
                    @click="onOpenScript(group.publicId)"
                >
                    <i class="fas fa-external-link-alt"></i>
                </button>
            </div>
            <ul class="compilation-errors-list">
                <li
                    v-for="(item, index) in group.errors"
                    :key="`${gIndex}-${index}`"
                    class="compilation-error-item"
                >
                    <span v-if="item.line > 0" class="compilation-error-line">
                        <template v-if="item.col">{{ $t('[[[Linia %0, poz. %1|||{0}|||{1}]]]', item.line, item.col) }}</template>
                        <template v-else>{{ $t('[[[Linia %0|||{0}]]]', item.line) }}</template>
                    </span>
                    <span v-if="item.line > 0" class="compilation-error-separator"> — </span>
                    <code class="compilation-error-message">{{ item.message }}</code>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.script-compilation-errors {
    background: var(--bs-danger-bg-subtle, rgba(220, 53, 69, 0.08));
    border: 1px solid var(--bs-danger-border-subtle, rgba(220, 53, 69, 0.25));
    border-radius: 0.375rem;
    padding: 0.75rem 1rem;
}

.compilation-errors-header {
    color: var(--bs-danger);
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
}

.compilation-errors-navigate {
    font-size: 0.9rem;
    color: var(--bs-body-color);
    font-weight: 500;
}

.compilation-nav-item {
    display: inline;
}

.compilation-nav-sep {
    color: inherit;
}

.compilation-errors-group {
    margin-bottom: 0.75rem;
}

.compilation-errors-group:last-child {
    margin-bottom: 0;
}

.compilation-errors-section-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--bs-body-color);
    font-family: var(--bs-font-monospace);
    margin-bottom: 0.25rem;
    padding-bottom: 0.15rem;
    border-bottom: 1px solid rgba(220, 53, 69, 0.15);
}

.compilation-open-script-btn {
    padding: 0.1rem 0.2rem;
    color: #212529;
    background: transparent;
    border: none;
    font-size: 0.8rem;
    line-height: 1;
    flex-shrink: 0;
}

.compilation-open-script-btn:hover {
    color: #000;
    opacity: 0.85;
}

.compilation-errors-list {
    margin: 0;
    padding-left: 1.25rem;
    list-style-type: none;
}

.compilation-errors-list li {
    position: relative;
    padding: 0.35rem 0;
    border-bottom: 1px solid rgba(220, 53, 69, 0.1);
    font-size: 0.85rem;
}

.compilation-errors-list li:last-child {
    border-bottom: none;
}

.compilation-errors-list li::before {
    content: '';
    position: absolute;
    left: -1rem;
    top: 0.65rem;
    width: 4px;
    height: 4px;
    background: var(--bs-danger);
    border-radius: 50%;
}

.compilation-error-line {
    font-size: 0.75rem;
    color: var(--bs-body-color);
    font-family: var(--bs-font-monospace);
    white-space: nowrap;
}

.compilation-error-separator {
    color: var(--bs-secondary);
    font-size: 0.8rem;
}

.compilation-error-message {
    background: rgba(0, 0, 0, 0.04);
    padding: 0.15rem 0.35rem;
    border-radius: 0.25rem;
    font-size: 0.8rem;
    color: var(--bs-body-color);
    border: 1px solid rgba(0, 0, 0, 0.06);
}
</style>
